import React, { useEffect, useState } from 'react';
import { Button, Flex, Text, CoveragesAccordion, ToggleButtonGroup, StarIcon, Card, LoaderSquare, BrandLogo } from 'src/components';
import styled from '@emotion/styled';
import { getBrandLogo, i18n, PropertyBrandWithProducts, PropertyProductWithCalculation, resolvePremiums, StandardizedCoverage, useCalculationInput } from 'src/common';
import { colors } from 'src/theme';

interface OfferCardProps {
  brandCalc: PropertyBrandWithProducts;
  initialProduct?: PropertyProductWithCalculation;
  onSelectOffer?: () => void;
  onSelectPackage?: (packageName: string) => void;
  calculationsLoading?: boolean;
  brandLoading?: boolean;
}

const OfferHeader = styled(Flex)`
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 24px;
  @media (min-width: 400px) {
    gap: 0;
    flex-direction: row;
    justify-content: space-between;
  }
`;

export const OfferCard: React.FC<OfferCardProps> = ({
  brandCalc,
  onSelectOffer,
  onSelectPackage,
  calculationsLoading,
  brandLoading,
  initialProduct
}) => {
  const [touched, setTouched] = useState<boolean>(false);
  const [brandProductCode, setBrandProductCode] = useState<string>(initialProduct?.code ?? '');
  const selectedProduct = brandCalc.products.find(prd => brandProductCode === prd.code) || brandCalc?.products?.[0];

  useEffect(() => {
    if (!calculationsLoading) setTouched(false);
  }, [calculationsLoading])

  const { setAdditionalCoverages, additionalCoverages } = useCalculationInput();

  const handleTogglePackage = (productCode: string) => {
    setBrandProductCode(productCode);
    onSelectPackage?.(productCode);
  };

  if (brandLoading) {
    return (
      <Card padding={8}>
        <Flex vertical gap={8} style={{ minHeight: 200 }}>
          <Flex  >
            <BrandLogo brandCode={brandCalc.code} src={getBrandLogo(brandCalc.code)} alt={brandCalc.code} />
          </Flex>
          <Flex vertical justify="center" align="center" style={{ flex: 1 }}>
            <LoaderSquare size={80} />
          </Flex>
          <Button
            type="button"
            btnType="primary"
            size="extralarge"
          />
        </Flex>
      </Card>
    );
  }

  if (!brandCalc.products.length || !selectedProduct) {
    return null;
  }

  const hasCalculation = !!selectedProduct.calculation;
  // Annual price uses the single-payment variant (5% annual discount applied).
  // Monthly price uses the installments variant (no discount) divided by 12.
  // totalPremium is the fallback when a variant is missing (see resolvePremiums).
  const { annual: annualPrice, monthly: monthlyPrice } = resolvePremiums(selectedProduct.calculation);

  const packageName = selectedProduct.calculation?.packageName ?? selectedProduct.name;
  // const packageDescription = offer.product.productTypeSpecific.packageDescription || i18n.t('components.cards.offerCard.basicInsurance');

  // Get coverage groups for the selected package
  // Groups coverages by main type (CONSTRUCTION vs HOUSEHOLD_CONTENTS)
  const getCoverageGroups = (product: PropertyProductWithCalculation): StandardizedCoverage[] => {
    const pkg = product?.calculation;

    if (!pkg) return []

    const groups: StandardizedCoverage[] = [];

    const constructionName = i18n.t('components.cards.offerCard.coverageConstruction');
    const householdName = i18n.t('components.cards.offerCard.coverageHousehold');
    const constructionCoverage = pkg.coverages!.find(c => c.name === constructionName || c.name === 'Građevinski dio');
    const householdCoverage = pkg.coverages!.find(c => c.name === householdName || c.name === 'Kućanstvo');

    const groupAdditionalCoverages = pkg.additionalCoverages.map((pAC) => {
      const franchise = pkg.additionalCoverages?.find((cov) => cov.code === 'earthquakeFranchise');
      if (pAC.code === 'earthquake' && franchise && Boolean(additionalCoverages.find((cov) => cov.coverageCode === 'earthquakeFranchise'))) {
        return {
          ...pAC,
          insuredAmount: franchise.insuredAmount,
          premium: franchise.premium
        }
      }
      return pAC
    }).filter((cov) => {
      if (additionalCoverages.find((aC) => (aC.coverageCode === cov.code))) return true;

      if (cov.code.includes('earthquake') && additionalCoverages.find((aC) => aC.coverageCode.includes('earthquake'))) return 1

      return 0;
    });

    groups.push({
      code: 'CONSTRUCTION',
      name: constructionName,
      insuredAmount: constructionCoverage?.insuredAmount,
      risks: constructionCoverage?.risks || [],
    });

    groups.push({
      code: 'HOUSEHOLD_CONTENTS',
      name: householdName,
      insuredAmount: householdCoverage?.insuredAmount,
      risks: householdCoverage?.risks || [],
    });

    return [groups.map(cov => ({ ...cov, isIncluded: true })), groupAdditionalCoverages].flat()
  };

  const coverageGroups = getCoverageGroups(selectedProduct);

  const handleToggleAdditionalCoverage = (brandCode: string, coverageCode: string) => {
    setTouched(true);
    setAdditionalCoverages(brandCode, coverageCode);
  };

  return (
    <Card padding={8} style={{
      paddingBottom: '16px'
    }}>
      <Flex vertical gap={8}>
        <Flex vertical gap={24}>
          <OfferHeader>
            <BrandLogo brandCode={brandCalc.code} src={getBrandLogo(brandCalc.code, brandCalc.ptsLogo)} alt={brandCalc.code} />
            <Flex style={{ width: 'fit-content' }}>
              { hasCalculation && (
                <ToggleButtonGroup
                  options={brandCalc.products.map((pkg, i) => ({
                    value: pkg.code ?? '',
                    label: pkg.calculation?.packageName ?? '',
                    icon: i === brandCalc.products.length - 1 ? <StarIcon height={16} width={16} /> : undefined
                  }))}
                  value={brandProductCode}
                  onChange={handleTogglePackage}
                />
              )}
            </Flex>
          </OfferHeader>
            { hasCalculation &&
          <Flex align="center" justify="space-between" style={{ width: '100%' }}>
            <Text type="sMedium" color={colors.text1} bold>{packageName} {i18n.t('components.cards.offerCard.packageSuffix')}</Text>
            { calculationsLoading && touched ? (
              <Flex style={{ paddingRight: 16 }}>
                <LoaderSquare />
              </Flex>
            ) : hasCalculation && (
              <Flex vertical align="flex-end" >
                <Flex align="baseline" justify="center">
                  <Text type="large" color={colors.text1} bold style={{ fontFamily: 'Red Hat Text, sans-serif' }}>
                    {monthlyPrice.toFixed(2).replace('.', ',')} €
                  </Text>
                  <Text type="base" color={colors.text4}>/{i18n.t('layout.headerData.perMonth')}</Text>
                </Flex>
                <Text type="tiny" color={colors.text4} style={{ textAlign: 'center' }}>
                  ({i18n.t('components.cards.offerCard.total')} {annualPrice.toFixed(2).replace('.', ',')} € {i18n.t('components.cards.offerCard.annually')})
                </Text>
              </Flex>
            )}
          </Flex>
          }
        </Flex>
        { hasCalculation && coverageGroups.length > 0 ? (
          <CoveragesAccordion
            coverageGroups={coverageGroups}
            selectedAdditionalCoverages={additionalCoverages}
            handleToggleCalc={(coverageCode: string) => handleToggleAdditionalCoverage(brandCalc.code, coverageCode)}
            disabled={true}
          />
        ) : (
            <>
              { selectedProduct.actionLabel && <Flex align="center" justify="center"  style={{
                background: colors.success2,
                borderRadius: 8,
                padding: 8,
                width: '100%'
              }}>
                <Text type="small" >{selectedProduct.actionLabel}</Text>
              </Flex> }
              <Flex align="center"  style={{
                background: colors.background6,
                borderRadius: 8,
                padding: 16,
                width: '100%'
              }}>
                <Text type="small" >Ponude ovog osiguranja mogu se dobiti samo pozivom agenta.</Text>
              </Flex>
            </>
        )}
      <Button
        onClick={onSelectOffer}
        type="button"
        btnType="primary"
        size="extralarge"
        text={i18n.t(`components.cards.offerCard.${hasCalculation ? 'contract' : 'contactAgent'}`)}
        />
      </Flex>
    </Card>
  );
};
